import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useCreateNocType, useUpdateNocType } from '../../hooks/use-noc-types';
import { nocTypeFormSchema, NocTypeFormValues } from '../../schemas/noc-type-form-schema';
import { NocType } from '../../types/noc-type.types';

export interface NocTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nocType?: NocType | null;
}

export const NocTypeFormDialog = ({ open, onOpenChange, nocType }: NocTypeFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(nocType);

  const { mutateAsync: createNocType, isPending: isCreating } = useCreateNocType();
  const { mutateAsync: updateNocType, isPending: isUpdating } = useUpdateNocType();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<NocTypeFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(nocTypeFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { Name: '', DocumentBody: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ Name: nocType?.Name ?? '', DocumentBody: nocType?.DocumentBody ?? '' });
  }, [open, nocType, form]);

  const handleSubmit = async (values: NocTypeFormValues) => {
    try {
      if (isEditMode && nocType) {
        await updateNocType({ itemId: nocType.ItemId, input: values });
      } else {
        await createNocType(values);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_NOC_TYPE') : t('CREATE_NOC_TYPE')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('NAME')} *</FormLabel>
                  <FormControl>
                    <Input placeholder={t('NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="DocumentBody"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('NOC_BODY')} *</FormLabel>
                  <FormControl>
                    <Textarea rows={8} placeholder={t('NOC_BODY')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
